import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Output,
	QueryList,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import {ChatBotService} from "../../../core/services/services/chat-bot.service";
import {employeeDataModel} from "./employeeData.model";
import {EmployeeRequestResultComponent} from "./employee-request-result/employee-request-result.component";
import {EmployeeService} from "src/app/core/services/services/employee.service";
import {firstValueFrom} from "rxjs/internal/firstValueFrom";

@Component({
	selector: "app-employee-data",
	templateUrl: "./employee-data.component.html",
	styleUrls: ["./employee-data.component.scss"],
	imports: [EmployeeRequestResultComponent],
})
export class EmployeeDataComponent implements AfterViewInit {
	@Output() animationTrigger = new EventEmitter<boolean>();

	triggerAnimation(value: boolean) {
		this.animationTrigger.emit(value);
	}

	@ViewChildren(EmployeeRequestResultComponent)
	employeeRequestResultsComponents!: QueryList<EmployeeRequestResultComponent>;
	@ViewChildren("buttons") selectionButtons!: QueryList<ElementRef>;
	@ViewChildren("requestResultDiv") requestResultDivs: QueryList<ElementRef>;
	@ViewChild("resultData") resultData!: ElementRef<HTMLDivElement>;
	private messageHandler: any;

	public employeeData: employeeDataModel = {
		positions: [
			{
				title: "",
				description: "",
				requestedQuantity: 0,
				requestedSkills: [],
				matchingEmployees: [],
			},
		],
	};

	public emptyEmployeeData: employeeDataModel = this.employeeData;

	ngAfterViewInit() {
		this.messageHandler.openConnection();

		this.messageHandler.messageSubject.subscribe((parsed) => {
			if (parsed.data) {
				const data = parsed.data.employeeData ? parsed.data.employeeData : parsed.data;
				this.employeeData = data;
				this.updateEmployeeData(this.employeeData);
				this.triggerAnimation(true);
			} else {
				this.employeeData.positions = [];
				this.triggerAnimation(false);
			}
		});

		this.selectionButtons.changes.subscribe(() => {
			if (this.selectionButtons.length > 0) {
				this.safeHandleButtonClick(0);
			}
		});
	}

	constructor(
		private chatBotService: ChatBotService,
		private employeeService: EmployeeService,
	) {
		this.messageHandler = this.chatBotService.getMessageHandler();
	}

	updateEmployeeData(employeeDataInput: employeeDataModel) {
		// Guard: if positions is not an array (e.g. out-of-scope LLM response), clear UI and skip processing
		if (!employeeDataInput || !Array.isArray(employeeDataInput.positions)) {
			this.employeeData = {positions: []} as employeeDataModel;
			// Update child components with empty skills to avoid indexing errors
			setTimeout(() => {
				this.employeeRequestResultsComponents.toArray().forEach((child) => {
					try {
						child.updateRequestedSkills([]);
					} catch (e) {
						// ignore if child not ready
					}
				});
			});
			setTimeout(() => this.safeHandleButtonClick(0), 0);
			return;
		}

		this.filterMatchingEmployees(employeeDataInput).then((filteredData) => {
			filteredData = this.trimExcessEmployees(filteredData);

			setTimeout(() => {
				this.employeeRequestResultsComponents.toArray().forEach((child, index) => {
					child.updateRequestedSkills(employeeDataInput.positions[index]?.requestedSkills ?? []);
				}, 0);
			});
			setTimeout(() => {
				this.safeHandleButtonClick(0);
			}, 0);
		});
	}

	handleButtonClick(buttonIndex: number) {
		this.setHighlightedButton(buttonIndex);
		this.requestResultDivs.get(buttonIndex).nativeElement.style.display = "flex";
		this.requestResultDivs
			.toArray()
			.filter((_, i) => i !== buttonIndex)
			.forEach((requestResultDiv) => {
				requestResultDiv.nativeElement.style.display = "none";
			});
	}

	private safeHandleButtonClick(index: number) {
		if (!this.selectionButtons || !this.requestResultDivs) return;

		const buttonsReady = this.selectionButtons.length > index;
		const divsReady = this.requestResultDivs.length > index;

		if (buttonsReady && divsReady) {
			this.handleButtonClick(index);
		}
	}

	setHighlightedButton(buttonNumber: number) {
		this.selectionButtons.get(buttonNumber)!.nativeElement.style.height = "100%";
		this.selectionButtons.get(buttonNumber)!.nativeElement.style.borderBottomLeftRadius = "0";
		this.selectionButtons.get(buttonNumber)!.nativeElement.style.borderBottomRightRadius = "0";
		this.selectionButtons.get(buttonNumber)!.nativeElement.style.boxShadow =
			"0 -5px 3px -3px rgba(0, 0, 0, 0.01), -5px 0 3px -3px rgba(0, 0, 0, 0.05), " +
			"5px 0 3px -3px rgba(0, 0, 0, 0.04), 0 5px 3px -3px rgba(0, 0, 0, 0)";

		this.selectionButtons
			.toArray()
			.filter((_, i) => i !== buttonNumber)
			.forEach((button) => {
				button.nativeElement.style.height = "95%";
				button.nativeElement.style.borderBottomLeftRadius = "var(--mep-container-border-radius)";
				button.nativeElement.style.borderBottomRightRadius = "var(--mep-container-border-radius)";
				button.nativeElement.style.boxShadow =
					"0 -5px 3px -3px rgba(0, 0, 0, 0.01), -5px 0 3px -3px rgba(0, 0, 0, 0.05), " +
					"5px 0 3px -3px rgba(0, 0, 0, 0.04), 0 5px 3px -3px rgba(0, 0, 0, 0.06)";
			});
		if (this.selectionButtons.length === 1) {
			this.resultData.nativeElement.style.borderTopLeftRadius = "0";
			this.resultData.nativeElement.style.borderTopRightRadius = "0";
		} else {
			switch (buttonNumber) {
				case 0:
					this.resultData.nativeElement.style.borderTopLeftRadius = "0";
					this.resultData.nativeElement.style.borderTopRightRadius = "var(--mep-container-border-radius)";
					break;
				case this.selectionButtons.length - 1:
					this.resultData.nativeElement.style.borderTopLeftRadius = "var(--mep-container-border-radius)";
					this.resultData.nativeElement.style.borderTopRightRadius = "0";
					break;
				default:
					this.resultData.nativeElement.style.borderTopLeftRadius = "var(--mep-container-border-radius)";
					this.resultData.nativeElement.style.borderTopRightRadius = "var(--mep-container-border-radius)";
					break;
			}
		}
	}

	trimExcessEmployees(data: employeeDataModel): employeeDataModel {
		if (!data || !Array.isArray(data.positions)) return data;

		data.positions.forEach((position) => {
			const maxAllowed = (position.requestedQuantity ?? 0) + 2;

			if (Array.isArray(position.matchingEmployees) && position.matchingEmployees.length > maxAllowed) {
				position.matchingEmployees = position.matchingEmployees.slice(0, maxAllowed);
			}
		});

		return data;
	}

	private async filterMatchingEmployees(data: employeeDataModel): Promise<employeeDataModel> {
		if (!data || !Array.isArray(data.positions)) return data;

		for (const position of data.positions) {
			const filteredEmployees: any[] = [];

			if (!Array.isArray(position.matchingEmployees)) {
				position.matchingEmployees = [];
				continue;
			}

			for (const matchingEmp of position.matchingEmployees) {
				try {
					const employee = await firstValueFrom(this.employeeService.getEmployeeByPersonnelNumber(matchingEmp.id));

					if (!employee) {
						continue;
					}

					// Filter: Nur Employees anzeigen, die noch nicht ausgetreten sind
					const heute = new Date();
					if (employee.leavingDate && new Date(employee.leavingDate) < heute) {
						// Employee ist ausgetreten - überspringen
						continue;
					}

					filteredEmployees.push(matchingEmp);
				} catch (error) {
					console.error("error on employee fetch", error);
					continue;
				}
			}

			position.matchingEmployees = filteredEmployees;
		}

		return data;
	}
}
